/**
 * Case File Component
 * 
 * Comprehensive case management hub showing:
 * - Progress flowchart with timeline
 * - Client profile information
 * - Attached files from conversation
 * - Documents with status
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import RequirementAttachModal from './RequirementAttachModal';
import { useToast } from '../../../Context/ToastContext';
import { useCapabilities } from '../../../Context/CapabilityContext';
import ExpirationRadar from '../ExpirationRadar/ExpirationRadar';
import ApplicationReview from '../ApplicationReview/ApplicationReview';
import TeamFeed from '../CaseProgress/TeamFeed';
import { FaClipboardList, FaCheckCircle, FaExclamationCircle, FaFileAlt, FaFolderOpen, FaArrowRight, FaUser, FaEnvelope, FaPhone, FaGlobe, FaCalendarAlt, FaPassport, FaHeart, FaMapMarkerAlt, FaExpand, FaEdit, FaDownload, FaClock, FaComment, FaFileUpload, FaUserCircle, FaClipboardCheck } from 'react-icons/fa';

import {
  Container, Header, HeaderLeft, HeaderTitle, ClientName, HeaderActions, ActionButton,
  ProgressBadge, MainContent, SectionTabs, SectionTab, SectionContent,
  FlowContainer, ProgressSplitContainer, ProgressTopPane, ResizeDivider, ProgressBottomPane,
  ProfileSection, ProfileGrid, ProfileCard, ProfileCardHeader, CardEditButton,
  ProfileCardContent, ProfileRow, ProfileLabel, ProfileValue,
  FilesSection, FilesList, FileCard, FileIcon, FileInfo, FileName, FileMeta, FileDate, FileSize, FileDownloadButton,
  DocumentsSection, DocumentsList, DocumentCard, DocumentIcon, DocumentInfo, DocumentName, DocumentMeta, DocumentStatus, DocumentProgress, ProgressBar, ProgressFill, ProgressText,
  EmptyState, LoadingState, LoadingSpinner,
  StartEndNode, MilestoneNodeWrapper, MilestoneHeader, MilestoneNumber, MilestoneTitle, MilestoneDeleteButton,
  StepsContainer, StepItem, DeleteButton, AddStepRow, StepCheckbox, StepText, StepTimeline,
  ModalOverlay, Modal, ModalTitle, ModalInput, ModalLabel, ModalActions, ModalButton,
} from './CaseFileStyles';

import {
  Icons, DEFAULT_TEMPLATE,
  getFileType, formatDate, getCleanFileName,
  AVATAR_COLORS, getInitials, getAvatarColor, generateId,
} from './caseFileConstants';

import useCaseData from './hooks/useCaseData';
import useProgressTemplate from './hooks/useProgressTemplate';
import ProfileEditModal from './ProfileEditModal';

// ============================================
// CUSTOM NODE COMPONENTS
// ============================================

const StartEndNodeComponent = ({ data }) => {
  return (
    <StartEndNode $isEnd={data.isEnd}>
      {data.isEnd && <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />}
      {data.label}
      {!data.isEnd && <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />}
    </StartEndNode>
  );
};

const MilestoneNodeComponent = ({ data }) => {
  const status = data.status || 'pending';
  
  return (
    <MilestoneNodeWrapper $status={status}>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      
      <MilestoneHeader $status={status}>
        <MilestoneNumber $status={status}>
          {(status === 'completed' || status === 'auto_detected') ? Icons.check : data.order}
        </MilestoneNumber>
        <MilestoneTitle $status={status}>{data.title}</MilestoneTitle>
        {data.editMode && (
          <MilestoneDeleteButton
            onClick={(e) => {
              e.stopPropagation();
              data.onDeleteMilestone?.(data.milestoneId);
            }}
            title="Delete milestone"
          >
            {Icons.trash}
          </MilestoneDeleteButton>
        )}
      </MilestoneHeader>
      
      <StepsContainer>
        {data.steps?.map((step) => (
          <StepItem 
            key={step.id}
            onClick={(e) => {
              e.stopPropagation();
              if (!data.editMode) {
                data.onStepClick?.(data.milestoneId, step.id);
              }
            }}
          >
            <StepCheckbox $status={step.status}>
              {(step.status === 'completed' || step.status === 'auto_detected') && Icons.check}
            </StepCheckbox>
            <StepText $status={step.status} title={step.text}>{step.text}</StepText>
            {step.completedAt && (
              <StepTimeline>
                {new Date(step.completedAt).toLocaleDateString()}
              </StepTimeline>
            )}
            {data.editMode && (
              <DeleteButton
                onClick={(e) => {
                  e.stopPropagation();
                  data.onDeleteStep?.(data.milestoneId, step.id);
                }}
                title="Delete step"
              >
                {Icons.trash}
              </DeleteButton>
            )}
          </StepItem>
        ))}
        
        {data.editMode && (
          <AddStepRow
            onClick={(e) => {
              e.stopPropagation();
              data.onAddStep?.(data.milestoneId);
            }}
          >
            {Icons.plus}
            <span>Add Step</span>
          </AddStepRow>
        )}
      </StepsContainer>
      
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </MilestoneNodeWrapper>
  );
};

const AddMilestoneNodeComponent = ({ data }) => {
  return (
    <div 
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: '2px dashed rgba(99, 102, 241, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: 'rgba(99, 102, 241, 0.9)',
        background: 'rgba(99, 102, 241, 0.1)',
        transition: 'all 0.2s ease'
      }}
      onClick={data.onClick}
      title="Add new milestone"
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      {Icons.plus}
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </div>
  );
};

const nodeTypes = {
  startEnd: StartEndNodeComponent,
  milestone: MilestoneNodeComponent,
  addMilestone: AddMilestoneNodeComponent,
};

// ============================================
// MAIN COMPONENT
// ============================================

const CaseFile = ({ threadId, conversation }) => {
  /**
   * Discipline gating. The Case File is the screen an adviser lives in, and four of its
   * eight tabs are immigration tooling — a financial adviser was being shown an
   * immigration program picker, an immigration expiry radar and an IRCC validation panel.
   * Features default to all-off while loading, so nothing immigration-shaped ever flashes
   * for a non-immigration adviser.
   */
  const { features, isImmigration } = useCapabilities();
  const SECTION_FEATURE = {
    program: 'immigrationPrograms',
    documents: 'irccForms',
    expirations: 'expirationTracker',
    applicationReview: 'applicationReview',
  };
  const sectionAllowed = React.useCallback(
    (section) => {
      const flag = SECTION_FEATURE[section];
      return !flag || features[flag] === true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [features]
  );

  const [activeSection, setActiveSection] = useState('portfolio');
  const [profileEditModal, setProfileEditModal] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Resize state for team notes pane
  const [teamNotesHeight, setTeamNotesHeight] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const splitContainerRef = React.useRef(null);
  
  const authenticatedAxios = useAuthenticatedAxios();
  const { showToast } = useToast();

  // ============================================
  // HOOKS
  // ============================================

  const {
    analysisData,
    files,
    documents,
    loading,
    applicationTypes,
    selectedProgram, setSelectedProgram,
    programChecklist, setProgramChecklist,
    userFormSessions,
    loadingProgram,
    pipelineName, setPipelineName,
    loadProgress,
    loadAnalysis,
    loadFiles,
    loadDocuments,
    loadSelectedProgram,
    loadApplicationTypes,
    loadUserFormSessions,
    loadProgramChecklist,
    linkRequirement,
    unlinkRequirement,
    verifyRequirement,
    sendDocumentRequest,
  } = useCaseData(threadId, authenticatedAxios);

  // Requirement attach modal (Phase 2)
  const [attachItem, setAttachItem] = useState(null);
  const refreshRequirements = async () => {
    await loadFiles();
    if (selectedProgram) await loadProgramChecklist(selectedProgram.typeId);
  };
  const handleAttachFile = async (file) => {
    await linkRequirement(selectedProgram.typeId, attachItem, file);
    await refreshRequirements();
    showToast?.(`Attached to "${attachItem.name}"`, 'success');
  };
  const handleUnlinkRequirement = async () => {
    await unlinkRequirement(selectedProgram.typeId, attachItem);
    await refreshRequirements();
    showToast?.(`Removed attachment from "${attachItem.name}"`, 'info');
  };
  const handleRequestFromClient = async () => {
    await sendDocumentRequest(selectedProgram.typeId, [attachItem]);
    await refreshRequirements();
    showToast?.(`Requested "${attachItem.name}" from the client`, 'success');
  };
  const handleVerifyRequirement = async () => {
    await verifyRequirement(selectedProgram.typeId, attachItem);
    await refreshRequirements();
    showToast?.(`Marked "${attachItem.name}" as verified`, 'success');
  };
  // Open the attached file in a new tab (best-effort via its stored URL).
  const handleViewFile = async (file) => {
    if (file?.fileUrl) { window.open(file.fileUrl, '_blank', 'noopener,noreferrer'); return; }
    try {
      const res = await authenticatedAxios.post('/conversation-files/download', { fileKey: file?.storageKey });
      if (res.data?.downloadUrl) window.open(res.data.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (e) { showToast?.('Could not open file', 'error'); }
  };

  const {
    template,
    editMode, setEditMode,
    modal, setModal,
    inputValue, setInputValue,
    nodes, edges, onNodesChange, onEdgesChange,
    progressPercentage,
    getMilestoneStatus,
    cycleStepStatus,
    addStep,
    saveStep,
    addMilestone,
    saveMilestone,
    deleteStep,
    deleteMilestone,
    onNodeDragStop,
    refreshProgress,
  } = useProgressTemplate(threadId, authenticatedAxios, loadProgress, setPipelineName);

  // Handle program selection - saves to database and assigns matching pipeline
  const handleSelectProgram = async (appType) => {
    setSelectedProgram(appType);
    loadProgramChecklist(appType.typeId);
    
    // Save to database
    if (threadId) {
      try {
        await authenticatedAxios.put(`/conversations/${threadId}/program`, {
          programId: appType.typeId
        });
        console.log('[CaseFile] Saved program selection:', appType.typeId);
        
        // Try to find and assign a matching pipeline from Synthora
        // Get ALL pipelines and do smart matching based on program name
        try {
          // Don't filter by category - get all pipelines to allow flexible matching
          const pipelinesRes = await authenticatedAxios.get(`/case-progress/${threadId}/available-pipelines`);
          const pipelines = pipelinesRes.data?.pipelines || [];
          console.log('[CaseFile] Available pipelines:', pipelines.length, pipelines.map(p => p.name));
          console.log('[CaseFile] Program selected:', appType.name, 'Category:', appType.category);
          
          if (pipelines.length > 0) {
            // Smart matching: look for pipeline that matches the program
            const programName = (appType.name || '').toLowerCase();
            const programCategory = (appType.category || '').toLowerCase();
            const programTypeId = (appType.typeId || '').toLowerCase();
            
            // Try to find a matching pipeline by various criteria
            let matchingPipeline = null;
            
            // 0. Direct keyword mapping for specific programs
            const keywordMap = {
              'pgwp': 'pgwp',
              'post-graduation': 'pgwp',
              'super visa': 'super visa',
              'super-visa': 'super visa',
              'parents and grandparents': 'super visa',
              'visitor visa': 'visitor visa',
              'study permit': 'study visa',
              'work permit': 'work permit',
              'express entry': 'express entry',
              'fswp': 'express entry',
              'federal skilled worker': 'express entry',
              'spousal': 'spousal sponsorship',
              'spouse': 'spousal sponsorship',
              'partner sponsorship': 'spousal sponsorship'
            };
            
            // Check keyword matches first
            for (const [keyword, pipelineCategory] of Object.entries(keywordMap)) {
              if (programName.includes(keyword) || programTypeId.includes(keyword)) {
                matchingPipeline = pipelines.find(p => 
                  p.category?.toLowerCase() === pipelineCategory ||
                  p.name?.toLowerCase().includes(pipelineCategory)
                );
                if (matchingPipeline) break;
              }
            }
            
            // 1. Exact name match in pipeline name (e.g., "Work Permit" in "Work Permit Pipeline")
            if (!matchingPipeline) {
              matchingPipeline = pipelines.find(p => 
                p.name.toLowerCase().includes(programName) ||
                programName.includes(p.name.toLowerCase().replace(' pipeline', ''))
              );
            }
            
            // 2. Check if program name contains key pipeline category words
            if (!matchingPipeline) {
              matchingPipeline = pipelines.find(p => {
                const pipelineCategory = (p.category || '').toLowerCase();
                return programName.includes(pipelineCategory) || 
                       pipelineCategory.includes(programName) ||
                       programCategory.includes(pipelineCategory) ||
                       pipelineCategory.includes(programCategory);
              });
            }
            
            // 3. Fall back to isDefault pipeline
            if (!matchingPipeline) {
              matchingPipeline = pipelines.find(p => p.isDefault);
            }
            
            // 4. If still no match, just use the first pipeline
            if (!matchingPipeline && pipelines.length > 0) {
              matchingPipeline = pipelines[0];
            }
            
            if (matchingPipeline) {
              console.log('[CaseFile] Assigning pipeline:', matchingPipeline.name, matchingPipeline.id);
              await authenticatedAxios.post(`/case-progress/${threadId}/assign-pipeline`, {
                pipelineId: matchingPipeline.id
              });
              console.log('[CaseFile] Auto-assigned pipeline:', matchingPipeline.name);
              showToast(`Pipeline "${matchingPipeline.name}" assigned to case progress`, 'success');
              
              // Dispatch event to notify CaseProgress to refresh
              window.dispatchEvent(new CustomEvent('pipelineAssigned', { 
                detail: { pipelineId: matchingPipeline.id, pipelineName: matchingPipeline.name }
              }));
            } else {
              console.log('[CaseFile] No pipelines available to assign');
            }
          } else {
            console.log('[CaseFile] No pipelines found for this expert');
          }
        } catch (pipelineError) {
          // Pipeline assignment is optional, don't fail the whole operation
          console.log('[CaseFile] Pipeline assignment error:', pipelineError.message);
          console.error('[CaseFile] Full error:', pipelineError);
        }
      } catch (error) {
        console.error('[CaseFile] Failed to save program selection:', error);
        showToast('Failed to save program selection', 'error');
      }
    }
  };

  // Handle clearing program selection - also saves to database
  const handleClearProgram = async () => {
    setSelectedProgram(null);
    setProgramChecklist([]);
    
    // Clear from database
    if (threadId) {
      try {
        await authenticatedAxios.put(`/conversations/${threadId}/program`, {
          programId: null
        });
        console.log('[CaseFile] Cleared program selection');
      } catch (error) {
        console.error('[CaseFile] Failed to clear program selection:', error);
      }
    }
  };

  // Refresh current section when tab changes
  const handleSectionChange = (section) => {
    if (!sectionAllowed(section)) return;   // gated tab: ignore rather than load its data
    setActiveSection(section);
    // Refresh data for the section
    if (section === 'portfolio') {
      // Portfolio loads all data
      loadAnalysis();
      loadFiles();
      loadDocuments();
      // Application types are the immigration program catalog — do not fetch them (and
      // trip the server-side capability gate) for an adviser who cannot use them.
      if (sectionAllowed('program')) loadApplicationTypes();
      loadUserFormSessions();
    }
    else if (section === 'program') {
      loadApplicationTypes();
      loadUserFormSessions();
      if (selectedProgram) loadProgramChecklist(selectedProgram.typeId);
    }
    else if (section === 'profile') loadAnalysis();
    else if (section === 'files') loadFiles();
    else if (section === 'documents') loadDocuments();
  };

  // Manual refresh for current section
  const handleRefresh = () => {
    if (activeSection === 'portfolio') {
      loadAnalysis();
      loadFiles();
      loadDocuments();
      if (sectionAllowed('program')) loadApplicationTypes();
      loadUserFormSessions();
    }
    else if (activeSection === 'program') {
      loadApplicationTypes();
      loadUserFormSessions();
      if (selectedProgram) loadProgramChecklist(selectedProgram.typeId);
    }
    else if (activeSection === 'progress') refreshProgress();
    else if (activeSection === 'profile') loadAnalysis();
    else if (activeSection === 'files') loadFiles();
    else if (activeSection === 'documents') loadDocuments();
  };

  // ============================================
  // FILE UPLOAD
  // ============================================

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !threadId) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('threadId', threadId);

    try {
      await authenticatedAxios.post('/conversation-files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Refresh files list
      await loadFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast('Failed to upload file. Please try again.', 'error');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ============================================
  // CLIENT PROFILE DATA EXTRACTION
  // ============================================

  const profileData = useMemo(() => {
    if (!analysisData) return null;
    
    // Extract from multiple possible sources (matching CaseDashboard logic)
    const clientProfile = analysisData.clientProfile || analysisData.personalDetails || {};
    const summary = analysisData.summary || {};
    
    // Known field mappings (fields we display in specific cards)
    const knownFields = new Set([
      'firstName', 'givenName', 'lastName', 'familyName', 'fullName',
      'email', 'phone', 'phoneNumber', 'telephone', 'phoneHome', 'phoneWork',
      'dateOfBirth', 'dob', 'gender', 'sex', 'maritalStatus', 'married',
      'nationality', 'citizenship', 'countryOfBirth', 'birthCountry', 'nativeLanguage', 'motherTongue',
      'currentLocation', 'location', 'currentCountry', 'countryOfResidence', 'currentCity', 'city', 'address',
      'occupation', 'currentOccupation', 'job', 'employer', 'company', 'workExperience',
      'education', 'educationLevel', 'institution', 'school', 'yearsOfEducation',
      'targetCountry', 'immigrationCategory', 'category', 'timeline', 'currentStatus', 'visaStatus',
      'passportNumber', 'passportExpiry', 'passportCountry',
      'canEnglish', 'englishAbility', 'canFrench', 'frenchAbility', 'languageTest',
      'familyMembers', 'dependents', 'spouse', 'partner'
    ]);
    
    // Find extra fields not in our known list
    const extraFields = {};
    Object.entries(clientProfile).forEach(([key, value]) => {
      if (!knownFields.has(key) && value !== null && value !== undefined && value !== '') {
        // Format the value for display
        if (typeof value === 'object') {
          extraFields[key] = JSON.stringify(value);
        } else {
          extraFields[key] = String(value);
        }
      }
    });
    
    return {
      // Basic Info
      firstName: clientProfile.firstName || clientProfile.givenName || summary?.clientName?.split(' ')[0] || null,
      lastName: clientProfile.lastName || clientProfile.familyName || summary?.clientName?.split(' ').slice(1).join(' ') || null,
      fullName: clientProfile.fullName || summary?.clientName || null,
      
      // Contact
      email: clientProfile.email || summary?.email || null,
      phone: clientProfile.phone || clientProfile.phoneNumber || clientProfile.telephone || summary?.phone || null,
      phoneHome: clientProfile.phoneHome || null,
      phoneWork: clientProfile.phoneWork || null,
      
      // Personal Details
      dateOfBirth: clientProfile.dateOfBirth || clientProfile.dob || null,
      gender: clientProfile.gender || clientProfile.sex || null,
      maritalStatus: clientProfile.maritalStatus || clientProfile.married || null,
      nationality: clientProfile.nationality || clientProfile.citizenship || summary?.nationality || null,
      countryOfBirth: clientProfile.countryOfBirth || clientProfile.birthCountry || null,
      nativeLanguage: clientProfile.nativeLanguage || clientProfile.motherTongue || null,
      
      // Location
      currentLocation: clientProfile.currentLocation || clientProfile.location || summary?.currentLocation || null,
      currentCountry: clientProfile.currentCountry || clientProfile.countryOfResidence || null,
      currentCity: clientProfile.currentCity || clientProfile.city || null,
      address: clientProfile.address || null,
      
      // Employment
      occupation: clientProfile.occupation || clientProfile.currentOccupation || clientProfile.job || summary?.employmentStatus || null,
      employer: clientProfile.employer || clientProfile.company || null,
      workExperience: clientProfile.workExperience || null,
      
      // Education
      education: clientProfile.education || clientProfile.educationLevel || null,
      institution: clientProfile.institution || clientProfile.school || null,
      yearsOfEducation: clientProfile.yearsOfEducation || null,
      
      // Immigration Specific
      targetCountry: clientProfile.targetCountry || summary?.targetCountry || null,
      immigrationCategory: clientProfile.immigrationCategory || summary?.category || null,
      timeline: clientProfile.timeline || summary?.timeline || null,
      currentStatus: clientProfile.currentStatus || summary?.currentStatus || null,
      visaStatus: clientProfile.visaStatus || null,
      
      // Passport
      passportNumber: clientProfile.passportNumber || null,
      passportExpiry: clientProfile.passportExpiry || null,
      passportCountry: clientProfile.passportCountry || null,
      
      // Language
      canEnglish: clientProfile.canEnglish || clientProfile.englishAbility || null,
      canFrench: clientProfile.canFrench || clientProfile.frenchAbility || null,
      languageTest: clientProfile.languageTest || null,
      
      // Family
      familyMembers: clientProfile.familyMembers || clientProfile.dependents || null,
      spouse: clientProfile.spouse || clientProfile.partner || null,
      
      // Extra fields not in our predefined list
      _extraFields: extraFields,
    };
  }, [analysisData]);

  // Count how many profile fields are filled (matching CaseDashboard logic)
  const filledProfileFields = profileData ? Object.values(profileData).filter(v => v !== null && v !== undefined && v !== '').length : 0;
  const hasProfileData = filledProfileFields > 0;

  // ============================================
  // FILE DOWNLOAD
  // ============================================

  const handleFileDownload = async (file) => {
    try {
      // If we already have a download URL, use it directly
      if (file.downloadUrl || file.url) {
        window.open(file.downloadUrl || file.url, '_blank');
        return;
      }
      
      // Get the file key - try multiple possible properties
      const fileKey = file.fileName || file.fileKey || file.key || file.s3Key || file.name || file.filename;
      
      if (!fileKey) {
        console.error('No file key found for file:', file);
        return;
      }
      
      const response = await authenticatedAxios.post('/conversation-files/download', {
        fileKey: fileKey,
        existingUrl: file.downloadUrl || file.url
      });
      
      if (response.data?.success && response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else if (response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // ============================================
  // RESIZE HANDLERS FOR TEAM NOTES PANE
  // ============================================

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !splitContainerRef.current) return;
    
    const containerRect = splitContainerRef.current.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const mouseY = e.clientY - containerRect.top;
    const newBottomHeight = containerHeight - mouseY;
    
    // Clamp between min and max heights
    const clampedHeight = Math.max(120, Math.min(containerHeight * 0.7, newBottomHeight));
    setTeamNotesHeight(clampedHeight);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add and remove mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const clientName = useMemo(() => {
    // Try profile data first
    if (profileData?.fullName) return profileData.fullName;
    if (profileData?.firstName || profileData?.lastName) {
      return `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();
    }
    // Try conversation userName
    if (conversation?.userName) return conversation.userName;
    // Try to find user from conversation's users array
    const clientUser = conversation?.users?.find(
      u => u.type === 'user' && !['widget', 'gpt', 'system'].includes(u.name?.toLowerCase())
    );
    if (clientUser?.name) return clientUser.name;
    // Last fallback
    return 'Client';
  }, [profileData, conversation]);

  // Empty state
  if (!threadId) {
    return (
      <Container>
        <EmptyState>
          {Icons.folder}
          <h3>No Case Selected</h3>
          <p>Select a user from the list to view their overview.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <HeaderTitle>
            {Icons.folder}
            Overview
          </HeaderTitle>
          <ClientName>{clientName}</ClientName>
          <ProgressBadge $percentage={progressPercentage}>
            {progressPercentage}% Complete
          </ProgressBadge>
        </HeaderLeft>
        
        <HeaderActions>
          {/* Refresh button for all sections */}
          <ActionButton onClick={handleRefresh} title="Refresh">
            {Icons.refresh}
            Refresh
          </ActionButton>
          
          {/* Progress section: Edit button */}
          {activeSection === 'progress' && (
            <ActionButton onClick={() => setEditMode(!editMode)}>
              {editMode ? Icons.check : Icons.edit}
              {editMode ? 'Done' : 'Edit'}
            </ActionButton>
          )}
          
          {/* Profile section: Add data button */}
          {activeSection === 'profile' && (
            <ActionButton $primary onClick={() => setProfileEditModal({ type: 'add' })}>
              {Icons.plus}
              Add Info
            </ActionButton>
          )}
          
          {/* Files section: Upload button */}
          {activeSection === 'files' && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <ActionButton 
                $primary 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
              >
                {uploadingFile ? 'Uploading...' : <>{Icons.upload} Attach File</>}
              </ActionButton>
            </>
          )}
        </HeaderActions>
      </Header>

      <SectionTabs>
        <SectionTab 
          $active={activeSection === 'portfolio'}
          onClick={() => handleSectionChange('portfolio')}
        >
          <FaUserCircle />
          Portfolio
        </SectionTab>
        {sectionAllowed('program') && (
        <SectionTab
          $active={activeSection === 'program'}
          onClick={() => handleSectionChange('program')}
        >
          <FaClipboardList />
          Program
        </SectionTab>
        )}
        <SectionTab 
          $active={activeSection === 'progress'}
          onClick={() => handleSectionChange('progress')}
        >
          {Icons.progress}
          Progress
        </SectionTab>
        <SectionTab 
          $active={activeSection === 'profile'}
          onClick={() => handleSectionChange('profile')}
        >
          {Icons.user}
          Client Profile
        </SectionTab>
        <SectionTab 
          $active={activeSection === 'files'}
          onClick={() => handleSectionChange('files')}
        >
          {Icons.file}
          Attachments
          {files.length > 0 && <span className="badge">{files.length}</span>}
        </SectionTab>
        {sectionAllowed('documents') && (
        <SectionTab
          $active={activeSection === 'documents'}
          onClick={() => handleSectionChange('documents')}
        >
          {Icons.document}
          Forms
          {documents.length > 0 && <span className="badge">{documents.length}</span>}
        </SectionTab>
        )}
        {sectionAllowed('expirations') && (
        <SectionTab
          $active={activeSection === 'expirations'}
          onClick={() => handleSectionChange('expirations')}
        >
          {Icons.radar}
          Deadlines
        </SectionTab>
        )}
        {sectionAllowed('applicationReview') && (
        <SectionTab
          $active={activeSection === 'applicationReview'}
          onClick={() => handleSectionChange('applicationReview')}
        >
          <FaClipboardCheck />
          Review
        </SectionTab>
        )}
      </SectionTabs>

      <MainContent>
        {/* Portfolio Section - Consolidated Client View */}
        <SectionContent $active={activeSection === 'portfolio'}>
          <div style={{ 
            height: '100%', 
            overflowY: 'auto',
            background: 'linear-gradient(180deg, rgba(17, 24, 39, 0.4) 0%, rgba(10, 13, 20, 0.95) 100%)',
            padding: '0'
          }}>
            {/* Client Header Card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.08) 100%)',
              borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
              padding: '24px 28px'
            }}>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* User Profile Picture - Same as user list but bigger */}
                {(() => {
                  const userIcon = conversation?.userIcon || 
                    conversation?.users?.find(u => u.picture)?.picture ||
                    conversation?.users?.[0]?.picture ||
                    conversation?.users?.[0]?.userIcon;
                  
                  if (userIcon) {
                    return (
                      <div style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        border: '3px solid rgba(99, 102, 241, 0.3)',
                        flexShrink: 0,
                        background: getAvatarColor(clientName)
                      }}>
                        <img 
                          src={userIcon}
                          alt="User"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.style.display = 'flex';
                            e.target.parentElement.style.alignItems = 'center';
                            e.target.parentElement.style.justifyContent = 'center';
                            e.target.parentElement.innerHTML = `<span style="color:#fff;font-size:36px;font-weight:600;text-transform:uppercase">${getInitials(clientName)}</span>`;
                          }}
                        />
                      </div>
                    );
                  }
                  
                  return (
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      border: '3px solid rgba(99, 102, 241, 0.3)',
                      flexShrink: 0,
                      background: getAvatarColor(clientName),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: '#fff', fontSize: '36px', fontWeight: 600, textTransform: 'uppercase' }}>
                        {getInitials(clientName)}
                      </span>
                    </div>
                  );
                })()}
                
                {/* Client Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h2 style={{ 
                        color: '#fff', 
                        margin: '0 0 4px 0', 
                        fontSize: '24px', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        {profileData?.fullName || `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim() || clientName || 'Client'}
                        <button
                          onClick={() => setProfileEditModal({ type: 'edit' })}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            color: '#9ca3af'
                          }}
                          title="Edit Profile"
                        >
                          <FaEdit size={12} />
                        </button>
                      </h2>
                      <div style={{ color: '#a5b4fc', fontSize: '14px', marginBottom: '8px' }}>
                        {selectedProgram?.name || 'No program selected'}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '13px', color: '#9ca3af' }}>
                        {profileData?.nationality && (
                          <span><FaGlobe style={{ marginRight: '6px', color: '#6366f1' }} />{profileData?.nationality}</span>
                        )}
                        {profileData?.dateOfBirth && (
                          <span><FaCalendarAlt style={{ marginRight: '6px', color: '#6366f1' }} />{profileData?.dateOfBirth}</span>
                        )}
                        {(profileData?.city || profileData?.country) && (
                          <span><FaMapMarkerAlt style={{ marginRight: '6px', color: '#6366f1' }} />{[profileData?.city, profileData?.country].filter(Boolean).join(', ')}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '20px',
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '12px',
                      padding: '12px 20px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#6366f1' }}>{documents.length}</div>
                        <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase' }}>Forms</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>{files.length}</div>
                        <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase' }}>Files</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>{progressPercentage}%</div>
                        <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase' }}>Progress</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Row */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '20px', 
                    marginTop: '16px',
                    padding: '12px 16px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}>
                    {profileData?.email && (
                      <span style={{ color: '#e8eaed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaEnvelope style={{ color: '#6366f1' }} /> {profileData?.email}
                      </span>
                    )}
                    {profileData?.phone && (
                      <span style={{ color: '#e8eaed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaPhone style={{ color: '#10b981' }} /> {profileData?.phone}
                      </span>
                    )}
                    {!profileData?.email && !profileData?.phone && (
                      <span style={{ color: '#6b7280' }}>No contact information yet</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Main Content Grid */}
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
              {/* Left Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* About / Personal Info Card */}
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(75, 85, 99, 0.3)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaUser style={{ color: '#6366f1' }} />
                      Personal Information
                    </h3>
                    <button
                      onClick={() => handleSectionChange('profile')}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: '#6366f1',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FaExpand size={10} /> Expand
                    </button>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                      {[
                        { icon: FaUser, label: 'Full Name', value: profileData?.fullName || `${profileData?.firstName || ''} ${profileData?.lastName || ''}`.trim() },
                        { icon: FaCalendarAlt, label: 'Date of Birth', value: profileData?.dateOfBirth },
                        { icon: FaHeart, label: 'Marital Status', value: profileData?.maritalStatus },
                        { icon: FaGlobe, label: 'Nationality', value: profileData?.nationality },
                        { icon: FaPassport, label: 'Passport No.', value: profileData?.passportNumber },
                        { icon: FaMapMarkerAlt, label: 'Country of Birth', value: profileData?.countryOfBirth },
                      ].filter(item => item.value).map((item, idx) => (
                        <div key={idx} style={{ 
                          padding: '12px',
                          background: 'rgba(31, 41, 55, 0.4)',
                          borderRadius: '8px'
                        }}>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <item.icon size={10} /> {item.label}
                          </div>
                          <div style={{ fontSize: '13px', color: '#e8eaed', fontWeight: '500' }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                    {!hasProfileData && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        No profile data yet. Information will appear as the client provides details.
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Program Status Card — immigration program selection + its checklist,
                    so it follows the same gate as the Program tab. */}
                {sectionAllowed('program') && (
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(75, 85, 99, 0.3)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaClipboardList style={{ color: '#10b981' }} />
                      Application Progress
                    </h3>
                    <button
                      onClick={() => handleSectionChange('program')}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: '#10b981',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FaExpand size={10} /> Expand
                    </button>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    {selectedProgram ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <div>
                            <div style={{ fontSize: '16px', color: '#fff', fontWeight: '600' }}>{selectedProgram.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{selectedProgram.estimatedProcessingTime}</div>
                          </div>
                          <div style={{
                            padding: '8px 16px',
                            background: `linear-gradient(135deg, ${progressPercentage >= 100 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'} 0%, transparent 100%)`,
                            borderRadius: '20px',
                            border: `1px solid ${progressPercentage >= 100 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                            color: progressPercentage >= 100 ? '#10b981' : '#f59e0b',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            {progressPercentage}% Complete
                          </div>
                        </div>
                        {/* Progress Bar */}
                        <div style={{ 
                          height: '8px', 
                          background: 'rgba(75, 85, 99, 0.3)', 
                          borderRadius: '4px', 
                          overflow: 'hidden',
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${progressPercentage}%`,
                            background: progressPercentage >= 100 
                              ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)' 
                              : 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                            borderRadius: '4px',
                            transition: 'width 0.5s ease'
                          }} />
                        </div>
                        {/* Quick checklist summary */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div style={{ padding: '12px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Forms</div>
                            <div style={{ fontSize: '18px', fontWeight: '600', color: '#6366f1' }}>
                              {programChecklist.filter(i => i.category === 'form' && i.completed).length} / {programChecklist.filter(i => i.category === 'form').length}
                            </div>
                          </div>
                          <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Documents</div>
                            <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981' }}>
                              {programChecklist.filter(i => i.category === 'document' && i.completed).length} / {programChecklist.filter(i => i.category === 'document').length}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        onClick={() => handleSectionChange('program')}
                        style={{ 
                          textAlign: 'center', 
                          padding: '30px', 
                          color: '#6b7280',
                          cursor: 'pointer',
                          background: 'rgba(99, 102, 241, 0.05)',
                          borderRadius: '12px',
                          border: '2px dashed rgba(99, 102, 241, 0.2)'
                        }}
                      >
                        <FaClipboardList size={32} style={{ marginBottom: '12px', color: '#6366f1' }} />
                        <div style={{ fontSize: '14px', color: '#9ca3af' }}>No program selected</div>
                        <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '8px' }}>Click to select a program →</div>
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Documents & Forms Card */}
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(75, 85, 99, 0.3)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaFileAlt style={{ color: '#f59e0b' }} />
                      Forms & Documents
                    </h3>
                    <button
                      onClick={() => handleSectionChange('documents')}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: '#f59e0b',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FaExpand size={10} /> Expand
                    </button>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    {documents.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {documents.slice(0, 4).map((doc, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            background: 'rgba(31, 41, 55, 0.4)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              width: '36px',
                              height: '36px',
                              background: 'rgba(245, 158, 11, 0.15)',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <FaFileAlt style={{ color: '#f59e0b' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '13px', color: '#e8eaed', fontWeight: '500' }}>{doc.name || doc.formName}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>{doc.status || 'Pending'}</div>
                            </div>
                            {doc.completionPercent !== undefined && (
                              <div style={{ 
                                padding: '4px 10px',
                                background: doc.completionPercent >= 100 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                                borderRadius: '12px',
                                fontSize: '11px',
                                color: doc.completionPercent >= 100 ? '#10b981' : '#6366f1'
                              }}>
                                {doc.completionPercent}%
                              </div>
                            )}
                          </div>
                        ))}
                        {documents.length > 4 && (
                          <div 
                            onClick={() => handleSectionChange('documents')}
                            style={{ 
                              textAlign: 'center', 
                              padding: '8px', 
                              color: '#6366f1', 
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            +{documents.length - 4} more forms →
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                        No forms attached yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Right Column - Timeline & Files */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Attachments Card */}
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(75, 85, 99, 0.3)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaFolderOpen style={{ color: '#ec4899' }} />
                      Attachments
                    </h3>
                    <button
                      onClick={() => handleSectionChange('files')}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(236, 72, 153, 0.3)',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        color: '#ec4899',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FaExpand size={10} /> Expand
                    </button>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    {files.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {files.slice(0, 5).map((file, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px',
                            background: 'rgba(31, 41, 55, 0.4)',
                            borderRadius: '8px'
                          }}>
                            <FaFileUpload style={{ color: '#ec4899', flexShrink: 0 }} />
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ fontSize: '12px', color: '#e8eaed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {getCleanFileName(file.originalName || file.displayName || file.fileName || file.name)}
                              </div>
                            </div>
                            <FaDownload 
                              style={{ color: '#6b7280', cursor: 'pointer', flexShrink: 0 }} 
                              size={12}
                              onClick={() => handleFileDownload(file)}
                            />
                          </div>
                        ))}
                        {files.length > 5 && (
                          <div 
                            onClick={() => handleSectionChange('files')}
                            style={{ textAlign: 'center', padding: '8px', color: '#ec4899', fontSize: '12px', cursor: 'pointer' }}
                          >
                            +{files.length - 5} more files →
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                        No files attached yet
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Activity Timeline Card */}
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(75, 85, 99, 0.3)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  flex: 1
                }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaClock style={{ color: '#3b82f6' }} />
                      Recent Activity
                    </h3>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Sample timeline items - these would come from actual activity data */}
                      {conversation?.createdAt && (
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            background: 'rgba(99, 102, 241, 0.15)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <FaComment style={{ color: '#6366f1', fontSize: '12px' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', color: '#e8eaed' }}>Case created</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              {new Date(conversation.createdAt).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                      {documents.length > 0 && (
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            background: 'rgba(245, 158, 11, 0.15)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <FaFileAlt style={{ color: '#f59e0b', fontSize: '12px' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', color: '#e8eaed' }}>{documents.length} form(s) attached</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>Forms ready for filling</div>
                          </div>
                        </div>
                      )}
                      {files.length > 0 && (
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            background: 'rgba(16, 185, 129, 0.15)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <FaFileUpload style={{ color: '#10b981', fontSize: '12px' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', color: '#e8eaed' }}>{files.length} file(s) uploaded</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>Supporting documents</div>
                          </div>
                        </div>
                      )}
                      {selectedProgram && (
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            background: 'rgba(139, 92, 246, 0.15)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <FaClipboardList style={{ color: '#8b5cf6', fontSize: '12px' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', color: '#e8eaed' }}>Program selected</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{selectedProgram.name}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Quick Actions Card */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                  borderRadius: '16px',
                  padding: '20px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', color: '#fff', fontSize: '14px' }}>Quick Actions</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button
                      onClick={() => handleSectionChange('program')}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'rgba(99, 102, 241, 0.2)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '8px',
                        color: '#a5b4fc',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <FaClipboardList /> View Program Checklist
                    </button>
                    <button
                      onClick={() => setProfileEditModal({ type: 'edit' })}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '8px',
                        color: '#6ee7b7',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <FaEdit /> Edit Client Profile
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'rgba(245, 158, 11, 0.2)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: '8px',
                        color: '#fcd34d',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <FaFileUpload /> Upload Document
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionContent>
        
        {/* Program Section - IRCC Application Type & Checklist */}
        <SectionContent $active={activeSection === 'program'}>
          <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
            {!selectedProgram ? (
              /* Program Selection */
              <div>
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '32px',
                  padding: '24px',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                  borderRadius: '16px',
                  border: '1px solid rgba(99, 102, 241, 0.2)'
                }}>
                  <FaClipboardList style={{ fontSize: '48px', color: '#6366f1', marginBottom: '16px' }} />
                  <h2 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '20px' }}>Select Immigration Program</h2>
                  <p style={{ color: '#9ca3af', margin: 0, fontSize: '14px' }}>
                    Choose the application type to see required forms, documents, and track progress
                  </p>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {applicationTypes.map(appType => (
                    <div 
                      key={appType.typeId}
                      onClick={() => handleSelectProgram(appType)}
                      style={{
                        padding: '20px',
                        background: 'rgba(31, 41, 55, 0.5)',
                        border: '1px solid rgba(75, 85, 99, 0.3)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'rgba(31, 41, 55, 0.5)';
                        e.currentTarget.style.borderColor = 'rgba(75, 85, 99, 0.3)';
                      }}
                    >
                      <h3 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600' }}>
                        {appType.name}
                      </h3>
                      <p style={{ color: '#9ca3af', margin: '0 0 12px 0', fontSize: '12px', lineHeight: '1.5' }}>
                        {appType.description}
                      </p>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#6b7280' }}>
                        <span><FaFileAlt style={{ marginRight: '4px' }} />{appType.requiredFormsCount} forms</span>
                        <span><FaFolderOpen style={{ marginRight: '4px' }} />{appType.requiredDocsCount} docs</span>
                      </div>
                      {appType.estimatedProcessingTime && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#a5b4fc' }}>
                          ⏱ {appType.estimatedProcessingTime}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Program Details & Checklist */
              <div>
                {/* Program Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '24px',
                  padding: '20px',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(99, 102, 241, 0.25)'
                }}>
                  <div>
                    <h2 style={{ color: '#fff', margin: '0 0 4px 0', fontSize: '18px' }}>
                      {selectedProgram.name}
                    </h2>
                    <p style={{ color: '#9ca3af', margin: 0, fontSize: '13px' }}>
                      {selectedProgram.description}
                    </p>
                  </div>
                  <button 
                    onClick={handleClearProgram}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(75, 85, 99, 0.5)',
                      border: '1px solid rgba(75, 85, 99, 0.5)',
                      borderRadius: '6px',
                      color: '#9ca3af',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Change Program
                  </button>
                </div>
                
                {loadingProgram ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                    Loading checklist...
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {/* Required Forms */}
                    <div style={{
                      padding: '20px',
                      background: 'rgba(17, 24, 39, 0.6)',
                      border: '1px solid rgba(75, 85, 99, 0.3)',
                      borderRadius: '12px'
                    }}>
                      <h3 style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaFileAlt style={{ color: '#6366f1' }} />
                        Required Forms
                      </h3>
                      {programChecklist.filter(item => item.category === 'form').map(item => (
                        <div 
                          key={item.id}
                          onClick={() => setAttachItem(item)}
                          title="Click to attach a file or request it from the client"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            marginBottom: '8px',
                            cursor: 'pointer',
                            background: item.completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(31, 41, 55, 0.4)',
                            border: `1px solid ${item.completed ? 'rgba(16, 185, 129, 0.25)' : 'rgba(75, 85, 99, 0.2)'}`,
                            borderRadius: '8px'
                          }}
                        >
                          {item.completed ? (
                            <FaCheckCircle style={{ color: '#10b981', flexShrink: 0 }} />
                          ) : (
                            <FaExclamationCircle style={{ color: item.required ? '#f59e0b' : '#6b7280', flexShrink: 0 }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#e8eaed', fontSize: '13px', fontWeight: '500' }}>{item.name}</div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>{item.description}</div>
                          </div>
                          {item.reqFiles && item.reqFiles.length > 0 ? (
                            <span title={item.reqFiles[0].fileName} style={{ padding: '2px 8px', background: 'rgba(16,185,129,0.2)', borderRadius: '4px', fontSize: '10px', color: '#10b981', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.reqStatus === 'verified' ? '✓ Verified' : `📎 ${item.reqFiles[0].fileName}`}
                            </span>
                          ) : item.reqStatus === 'requested' ? (
                            <span style={{ padding: '2px 8px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '4px', fontSize: '10px', color: '#60a5fa' }}>
                              Requested
                            </span>
                          ) : (!item.completed && item.required) ? (
                            <span style={{ padding: '2px 8px', background: 'rgba(245, 158, 11, 0.2)', borderRadius: '4px', fontSize: '10px', color: '#f59e0b' }}>
                              Required
                            </span>
                          ) : null}
                        </div>
                      ))}
                      {programChecklist.filter(item => item.category === 'form').length === 0 && (
                        <div style={{ color: '#6b7280', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                          No forms configured
                        </div>
                      )}
                    </div>
                    
                    {/* Required Documents */}
                    <div style={{
                      padding: '20px',
                      background: 'rgba(17, 24, 39, 0.6)',
                      border: '1px solid rgba(75, 85, 99, 0.3)',
                      borderRadius: '12px'
                    }}>
                      <h3 style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaFolderOpen style={{ color: '#10b981' }} />
                        Required Documents
                      </h3>
                      {programChecklist.filter(item => item.category === 'document').map(item => (
                        <div 
                          key={item.id}
                          onClick={() => setAttachItem(item)}
                          title="Click to attach a file or request it from the client"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            marginBottom: '8px',
                            cursor: 'pointer',
                            background: item.completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(31, 41, 55, 0.4)',
                            border: `1px solid ${item.completed ? 'rgba(16, 185, 129, 0.25)' : 'rgba(75, 85, 99, 0.2)'}`,
                            borderRadius: '8px'
                          }}
                        >
                          {item.completed ? (
                            <FaCheckCircle style={{ color: '#10b981', flexShrink: 0 }} />
                          ) : (
                            <FaExclamationCircle style={{ color: item.required ? '#f59e0b' : '#6b7280', flexShrink: 0 }} />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#e8eaed', fontSize: '13px', fontWeight: '500' }}>{item.name}</div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>{item.description}</div>
                          </div>
                          {item.reqFiles && item.reqFiles.length > 0 ? (
                            <span title={item.reqFiles[0].fileName} style={{ padding: '2px 8px', background: 'rgba(16,185,129,0.2)', borderRadius: '4px', fontSize: '10px', color: '#10b981', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.reqStatus === 'verified' ? '✓ Verified' : `📎 ${item.reqFiles[0].fileName}`}
                            </span>
                          ) : item.reqStatus === 'requested' ? (
                            <span style={{ padding: '2px 8px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '4px', fontSize: '10px', color: '#60a5fa' }}>
                              Requested
                            </span>
                          ) : (!item.completed && item.required) ? (
                            <span style={{ padding: '2px 8px', background: 'rgba(245, 158, 11, 0.2)', borderRadius: '4px', fontSize: '10px', color: '#f59e0b' }}>
                              Required
                            </span>
                          ) : null}
                        </div>
                      ))}
                      {programChecklist.filter(item => item.category === 'document').length === 0 && (
                        <div style={{ color: '#6b7280', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                          No documents configured
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Next Steps Summary */}
                {!loadingProgram && programChecklist.length > 0 && (
                  <div style={{
                    marginTop: '20px',
                    padding: '20px',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
                    border: '1px solid rgba(245, 158, 11, 0.25)',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{ color: '#f59e0b', margin: '0 0 12px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaArrowRight />
                      Action Items
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {programChecklist.filter(item => !item.completed && item.required).slice(0, 5).map((item, idx) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', color: '#e8eaed', fontSize: '13px' }}>
                          <span style={{ 
                            width: '20px', 
                            height: '20px', 
                            background: 'rgba(245, 158, 11, 0.3)', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: '600',
                            flexShrink: 0,
                            marginTop: '2px'
                          }}>
                            {idx + 1}
                          </span>
                          <div>
                            <div style={{ fontWeight: '500' }}>
                              {item.category === 'form' 
                                ? `Complete form: ${item.name}` 
                                : `Missing document: ${item.name}`}
                            </div>
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                              {item.category === 'form' 
                                ? 'Send questionnaire to client or fill manually' 
                                : 'Request client to provide this document'}
                            </div>
                          </div>
                        </div>
                      ))}
                      {programChecklist.filter(item => !item.completed && item.required).length === 0 && (
                        <div style={{ color: '#10b981', fontSize: '13px' }}>
                          ✓ All required items are complete! Ready for submission.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SectionContent>
        
        {/* Progress Section - Split layout with Flow + Team Notes */}
        <SectionContent $active={activeSection === 'progress'}>
          <ProgressSplitContainer ref={splitContainerRef}>
            {/* Top Pane: Progress Flowchart */}
            <ProgressTopPane>
              {/* Pipeline Name Header */}
              {(pipelineName || template.name) && (
                <div style={{
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
                  borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a5b4fc" strokeWidth="2">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                    </svg>
                    <span style={{ 
                      color: '#fff', 
                      fontSize: '15px', 
                      fontWeight: '600'
                    }}>
                      {pipelineName || template.name}
                    </span>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    background: 'rgba(99, 102, 241, 0.2)',
                    borderRadius: '12px',
                    fontSize: '11px',
                    color: '#a5b4fc',
                    fontWeight: '500'
                  }}>
                    {template.milestones?.length || 0} stages
                  </span>
                </div>
              )}
              <FlowContainer>
                {activeSection === 'progress' && nodes.length > 0 && (
                  <ReactFlow
                    key={`flow-${threadId}-${nodes.length}`}
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeDragStop={onNodeDragStop}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ 
                      padding: 0.3,
                      includeHiddenNodes: false,
                      minZoom: 0.4,
                      maxZoom: 1
                    }}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
                    minZoom={0.2}
                    maxZoom={1.5}
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background color="rgba(75, 85, 99, 0.15)" gap={20} />
                    <Controls showInteractive={false} />
                  </ReactFlow>
                )}
                {activeSection === 'progress' && nodes.length === 0 && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%', 
                    color: '#9ca3af',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <span>No progress data available</span>
                    <button 
                      onClick={refreshProgress}
                      style={{
                        background: 'rgba(99, 102, 241, 0.2)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        color: '#818cf8',
                        cursor: 'pointer'
                      }}
                    >
                      Load Progress
                    </button>
                  </div>
                )}
              </FlowContainer>
            </ProgressTopPane>

            {/* Resize Divider - Drag to resize */}
            <ResizeDivider onMouseDown={handleResizeStart} />

            {/* Bottom Pane: Team Notes Feed */}
            <ProgressBottomPane $height={teamNotesHeight}>
              <TeamFeed 
                threadId={threadId}
                conversationId={conversation?._id}
                clientName={clientName}
              />
            </ProgressBottomPane>
          </ProgressSplitContainer>
        </SectionContent>

        {/* Client Profile Section */}
        <SectionContent $active={activeSection === 'profile'}>
          {loading.analysis ? (
            <LoadingState>
              <LoadingSpinner />
              <span style={{ color: '#9ca3af' }}>Loading client profile...</span>
            </LoadingState>
          ) : !hasProfileData ? (
            <EmptyState>
              {Icons.user}
              <h3>No Profile Data</h3>
              <p>Client information will appear here once gathered from conversations.</p>
            </EmptyState>
          ) : (
            <ProfileSection>
              <ProfileGrid>
                {/* Personal Info Card */}
                <ProfileCard>
                  <ProfileCardHeader>
                    {Icons.user}
                    <h3>Personal Information</h3>
                    <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                      {Icons.pencil}
                    </CardEditButton>
                  </ProfileCardHeader>
                  <ProfileCardContent>
                    {profileData.fullName && <ProfileRow><ProfileLabel>Full Name</ProfileLabel><ProfileValue>{profileData.fullName}</ProfileValue></ProfileRow>}
                    {(!profileData.fullName && (profileData.firstName || profileData.lastName)) && <ProfileRow><ProfileLabel>Name</ProfileLabel><ProfileValue>{`${profileData.firstName || ''} ${profileData.lastName || ''}`.trim()}</ProfileValue></ProfileRow>}
                    {profileData.dateOfBirth && <ProfileRow><ProfileLabel>Date of Birth</ProfileLabel><ProfileValue>{profileData.dateOfBirth}</ProfileValue></ProfileRow>}
                    {profileData.gender && <ProfileRow><ProfileLabel>Gender</ProfileLabel><ProfileValue>{profileData.gender}</ProfileValue></ProfileRow>}
                    {profileData.maritalStatus && <ProfileRow><ProfileLabel>Marital Status</ProfileLabel><ProfileValue>{profileData.maritalStatus}</ProfileValue></ProfileRow>}
                    {profileData.nationality && <ProfileRow><ProfileLabel>Nationality</ProfileLabel><ProfileValue>{profileData.nationality}</ProfileValue></ProfileRow>}
                    {profileData.countryOfBirth && <ProfileRow><ProfileLabel>Country of Birth</ProfileLabel><ProfileValue>{profileData.countryOfBirth}</ProfileValue></ProfileRow>}
                    {profileData.nativeLanguage && <ProfileRow><ProfileLabel>Native Language</ProfileLabel><ProfileValue>{profileData.nativeLanguage}</ProfileValue></ProfileRow>}
                  </ProfileCardContent>
                </ProfileCard>

                {/* Contact Info Card */}
                <ProfileCard>
                  <ProfileCardHeader>
                    {Icons.mail}
                    <h3>Contact Information</h3>
                    <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                      {Icons.pencil}
                    </CardEditButton>
                  </ProfileCardHeader>
                  <ProfileCardContent>
                    {profileData.email && <ProfileRow><ProfileLabel>Email</ProfileLabel><ProfileValue>{profileData.email}</ProfileValue></ProfileRow>}
                    {profileData.phone && <ProfileRow><ProfileLabel>Phone</ProfileLabel><ProfileValue>{profileData.phone}</ProfileValue></ProfileRow>}
                    {profileData.phoneHome && <ProfileRow><ProfileLabel>Home Phone</ProfileLabel><ProfileValue>{profileData.phoneHome}</ProfileValue></ProfileRow>}
                    {profileData.phoneWork && <ProfileRow><ProfileLabel>Work Phone</ProfileLabel><ProfileValue>{profileData.phoneWork}</ProfileValue></ProfileRow>}
                    {profileData.address && <ProfileRow><ProfileLabel>Address</ProfileLabel><ProfileValue>{profileData.address}</ProfileValue></ProfileRow>}
                    {profileData.currentLocation && <ProfileRow><ProfileLabel>Location</ProfileLabel><ProfileValue>{profileData.currentLocation}</ProfileValue></ProfileRow>}
                    {profileData.currentCity && <ProfileRow><ProfileLabel>City</ProfileLabel><ProfileValue>{profileData.currentCity}</ProfileValue></ProfileRow>}
                    {profileData.currentCountry && <ProfileRow><ProfileLabel>Country</ProfileLabel><ProfileValue>{profileData.currentCountry}</ProfileValue></ProfileRow>}
                  </ProfileCardContent>
                </ProfileCard>

                {/* Employment Card */}
                <ProfileCard>
                  <ProfileCardHeader>
                    {Icons.briefcase}
                    <h3>Employment</h3>
                    <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                      {Icons.pencil}
                    </CardEditButton>
                  </ProfileCardHeader>
                  <ProfileCardContent>
                    {profileData.occupation && <ProfileRow><ProfileLabel>Occupation</ProfileLabel><ProfileValue>{profileData.occupation}</ProfileValue></ProfileRow>}
                    {profileData.employer && <ProfileRow><ProfileLabel>Employer</ProfileLabel><ProfileValue>{profileData.employer}</ProfileValue></ProfileRow>}
                    {profileData.workExperience && <ProfileRow><ProfileLabel>Work Experience</ProfileLabel><ProfileValue>{profileData.workExperience}</ProfileValue></ProfileRow>}
                  </ProfileCardContent>
                </ProfileCard>

                {/* Education Card */}
                <ProfileCard>
                  <ProfileCardHeader>
                    {Icons.book}
                    <h3>Education</h3>
                    <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                      {Icons.pencil}
                    </CardEditButton>
                  </ProfileCardHeader>
                  <ProfileCardContent>
                    {profileData.education && <ProfileRow><ProfileLabel>Education</ProfileLabel><ProfileValue>{profileData.education}</ProfileValue></ProfileRow>}
                    {profileData.institution && <ProfileRow><ProfileLabel>Institution</ProfileLabel><ProfileValue>{profileData.institution}</ProfileValue></ProfileRow>}
                    {profileData.yearsOfEducation && <ProfileRow><ProfileLabel>Years of Education</ProfileLabel><ProfileValue>{profileData.yearsOfEducation}</ProfileValue></ProfileRow>}
                  </ProfileCardContent>
                </ProfileCard>

                {/* Immigration Card.
                    Only its ROWS were guarded before, so the header rendered for every
                    discipline — a financial adviser saw an empty "Immigration" card. Now
                    it needs an immigration discipline AND at least one populated field,
                    so it also stops being an empty shell on a fresh immigration case.
                    (Family and Additional Information are already mounted this way.) */}
                {isImmigration && [
                  'targetCountry', 'immigrationCategory', 'currentStatus', 'visaStatus',
                  'passportNumber', 'passportExpiry', 'passportCountry',
                ].some((k) => profileData[k]) && (
                <ProfileCard>
                  <ProfileCardHeader>
                    {Icons.globe}
                    <h3>Immigration</h3>
                    <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                      {Icons.pencil}
                    </CardEditButton>
                  </ProfileCardHeader>
                  <ProfileCardContent>
                    {profileData.targetCountry && <ProfileRow><ProfileLabel>Target Country</ProfileLabel><ProfileValue>{profileData.targetCountry}</ProfileValue></ProfileRow>}
                    {profileData.immigrationCategory && <ProfileRow><ProfileLabel>Category</ProfileLabel><ProfileValue>{profileData.immigrationCategory}</ProfileValue></ProfileRow>}
                    {profileData.currentStatus && <ProfileRow><ProfileLabel>Current Status</ProfileLabel><ProfileValue>{profileData.currentStatus}</ProfileValue></ProfileRow>}
                    {profileData.visaStatus && <ProfileRow><ProfileLabel>Visa Status</ProfileLabel><ProfileValue>{profileData.visaStatus}</ProfileValue></ProfileRow>}
                    {profileData.timeline && <ProfileRow><ProfileLabel>Timeline</ProfileLabel><ProfileValue>{profileData.timeline}</ProfileValue></ProfileRow>}
                    {profileData.passportNumber && <ProfileRow><ProfileLabel>Passport #</ProfileLabel><ProfileValue>{profileData.passportNumber}</ProfileValue></ProfileRow>}
                    {profileData.passportExpiry && <ProfileRow><ProfileLabel>Passport Expiry</ProfileLabel><ProfileValue>{profileData.passportExpiry}</ProfileValue></ProfileRow>}
                    {profileData.passportCountry && <ProfileRow><ProfileLabel>Passport Country</ProfileLabel><ProfileValue>{profileData.passportCountry}</ProfileValue></ProfileRow>}
                  </ProfileCardContent>
                </ProfileCard>
                )}

                {/* Languages Card */}
                <ProfileCard>
                  <ProfileCardHeader>
                    {Icons.globe}
                    <h3>Languages</h3>
                    <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                      {Icons.pencil}
                    </CardEditButton>
                  </ProfileCardHeader>
                  <ProfileCardContent>
                    {profileData.canEnglish && <ProfileRow><ProfileLabel>English</ProfileLabel><ProfileValue>{profileData.canEnglish}</ProfileValue></ProfileRow>}
                    {profileData.canFrench && <ProfileRow><ProfileLabel>French</ProfileLabel><ProfileValue>{profileData.canFrench}</ProfileValue></ProfileRow>}
                    {profileData.languageTest && <ProfileRow><ProfileLabel>Language Test</ProfileLabel><ProfileValue>{profileData.languageTest}</ProfileValue></ProfileRow>}
                  </ProfileCardContent>
                </ProfileCard>

                {/* Family Card */}
                {(profileData.familyMembers || profileData.spouse) && (
                  <ProfileCard>
                    <ProfileCardHeader>
                      {Icons.user}
                      <h3>Family</h3>
                      <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                        {Icons.pencil}
                      </CardEditButton>
                    </ProfileCardHeader>
                    <ProfileCardContent>
                      {profileData.spouse && <ProfileRow><ProfileLabel>Spouse</ProfileLabel><ProfileValue>{profileData.spouse}</ProfileValue></ProfileRow>}
                      {profileData.familyMembers && <ProfileRow><ProfileLabel>Family Members</ProfileLabel><ProfileValue>{profileData.familyMembers}</ProfileValue></ProfileRow>}
                    </ProfileCardContent>
                  </ProfileCard>
                )}

                {/* Additional Information Card - shows any extra fields from AI */}
                {profileData._extraFields && Object.keys(profileData._extraFields).length > 0 && (
                  <ProfileCard>
                    <ProfileCardHeader>
                      {Icons.file}
                      <h3>Additional Information</h3>
                      <CardEditButton onClick={() => setProfileEditModal({ type: 'edit' })} title="Edit">
                        {Icons.pencil}
                      </CardEditButton>
                    </ProfileCardHeader>
                    <ProfileCardContent>
                      {Object.entries(profileData._extraFields).map(([key, value]) => (
                        <ProfileRow key={key}>
                          <ProfileLabel>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}</ProfileLabel>
                          <ProfileValue>{value}</ProfileValue>
                        </ProfileRow>
                      ))}
                    </ProfileCardContent>
                  </ProfileCard>
                )}
              </ProfileGrid>
            </ProfileSection>
          )}
        </SectionContent>

        {/* Files Section */}
        <SectionContent $active={activeSection === 'files'}>
          {loading.files ? (
            <LoadingState>
              <LoadingSpinner />
              <span style={{ color: '#9ca3af' }}>Loading files...</span>
            </LoadingState>
          ) : files.length === 0 ? (
            <EmptyState>
              {Icons.file}
              <h3>No Files Attached</h3>
              <p>Files uploaded in conversations will appear here.</p>
            </EmptyState>
          ) : (
            <FilesSection>
              <FilesList>
                {files.map((file, index) => {
                  const rawFileName = file.originalName || file.displayName || file.fileName || file.name || file.filename || 'Unknown file';
                  const fileType = getFileType(rawFileName);
                  const fileName = getCleanFileName(rawFileName);
                  const fileExt = getFileType(rawFileName).toUpperCase();
                  return (
                    <FileCard key={file._id || index} onClick={() => handleFileDownload(file)}>
                      <FileIcon $type={fileType}>
                        {fileType === 'pdf' ? Icons.pdf : 
                         fileType === 'image' ? Icons.image : Icons.file}
                      </FileIcon>
                      <FileInfo>
                        <FileName>{fileName}</FileName>
                        <FileMeta>
                          <FileDate>{formatDate(file.uploadedAt || file.createdAt || file.time)}</FileDate>
                          <span>•</span>
                          <FileSize>{fileExt} file</FileSize>
                        </FileMeta>
                      </FileInfo>
                      <FileDownloadButton>
                        {Icons.download}
                      </FileDownloadButton>
                    </FileCard>
                  );
                })}
              </FilesList>
            </FilesSection>
          )}
        </SectionContent>

        {/* Forms Section */}
        <SectionContent $active={activeSection === 'documents'}>
          {loading.documents ? (
            <LoadingState>
              <LoadingSpinner />
              <span style={{ color: '#9ca3af' }}>Loading forms...</span>
            </LoadingState>
          ) : documents.length === 0 ? (
            <EmptyState>
              {Icons.document}
              <h3>No Forms</h3>
              <p>Forms created from templates will appear here.</p>
            </EmptyState>
          ) : (
            <DocumentsSection>
              <DocumentsList>
                {documents.map((doc) => (
                  <DocumentCard key={doc._id}>
                    <DocumentIcon>
                      {Icons.document}
                    </DocumentIcon>
                    <DocumentInfo>
                      <DocumentName>{doc.templateName}</DocumentName>
                      <DocumentMeta>
                        <span>{doc.formCode}</span>
                        <span>•</span>
                        <span>{formatDate(doc.updatedAt || doc.createdAt)}</span>
                      </DocumentMeta>
                    </DocumentInfo>
                    <DocumentProgress>
                      <ProgressBar>
                        <ProgressFill $percent={doc.completionPercent || 0} />
                      </ProgressBar>
                      <ProgressText>{doc.completionPercent || 0}%</ProgressText>
                    </DocumentProgress>
                    <DocumentStatus $status={doc.status}>
                      {doc.status === 'verified' && Icons.check}
                      {doc.status?.charAt(0).toUpperCase() + doc.status?.slice(1) || 'Draft'}
                    </DocumentStatus>
                  </DocumentCard>
                ))}
              </DocumentsList>
            </DocumentsSection>
          )}
        </SectionContent>

        {/* Expirations Section - Deadline Radar.
            Mounted only when allowed: these children fetch on mount, so leaving them
            rendered-but-hidden would still call gated endpoints. */}
        {sectionAllowed('expirations') && (
        <SectionContent $active={activeSection === 'expirations'}>
          <ExpirationRadar
            conversationId={threadId}
            clientName={clientName}
          />
        </SectionContent>
        )}

        {/* Application Review Section */}
        {sectionAllowed('applicationReview') && (
        <SectionContent $active={activeSection === 'applicationReview'}>
          <ApplicationReview
            threadId={threadId}
            conversation={conversation}
          />
        </SectionContent>
        )}
      </MainContent>

      {/* Requirement attach modal (Phase 2) */}
      {attachItem && (
        <RequirementAttachModal
          item={attachItem}
          files={files}
          threadId={threadId}
          authenticatedAxios={authenticatedAxios}
          onAttachFile={handleAttachFile}
          onUnlink={handleUnlinkRequirement}
          onRequest={handleRequestFromClient}
          onVerify={handleVerifyRequirement}
          onViewFile={handleViewFile}
          onClose={() => setAttachItem(null)}
        />
      )}

      {/* Modal */}
      {/* Progress Modal (Add Milestone/Step) */}
      {modal && (
        <ModalOverlay onClick={() => setModal(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>
              {modal.type === 'milestone' ? 'Add Milestone' : 'Add Step'}
            </ModalTitle>
            
            <ModalLabel>
              {modal.type === 'milestone' ? 'Milestone Name' : 'Step Description'}
            </ModalLabel>
            <ModalInput
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder={modal.type === 'milestone' ? 'e.g., Final Review' : 'e.g., Upload passport'}
              autoFocus
              onKeyPress={e => e.key === 'Enter' && (modal.type === 'milestone' ? saveMilestone() : saveStep())}
            />
            
            <ModalActions>
              <ModalButton onClick={() => setModal(null)}>Cancel</ModalButton>
              <ModalButton $primary onClick={modal.type === 'milestone' ? saveMilestone : saveStep}>
                Add
              </ModalButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}

      {/* Profile Edit Modal */}
      {profileEditModal && (
        <ProfileEditModal 
          onClose={() => setProfileEditModal(null)}
          threadId={threadId}
          authenticatedAxios={authenticatedAxios}
          onSave={() => {
            setProfileEditModal(null);
            loadAnalysis();
          }}
          existingData={profileData}
        />
      )}
    </Container>
  );
};

export default CaseFile;
